import { type ReactNode, useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import toast from "components/partials/toast/toast";
import { FormDialog } from "ui/form-dialog";
import SelectedTouchpointsFolderModal from "containers/admin/clients/campaign/campaign-creative-page/components/selected-touchpoints-folder-modal/selected-touchpoints-folder-modal";
import CampaignFolderSelect from "components/partials/campaign-folder-select/campaign-folder-select";
import { useAddTouchpointToFolder } from "hooks/api/use-folders";
import { folderKeys, touchpointKeys } from "hooks/api/query-keys";
import { getErrorMessage } from "utilities";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";

interface UseAddTouchpointToFolderModalProps {
  onSuccess?: () => void;
  onRemoveSelectedItem?: (id: string) => void;
}

type SingleItemFolderDialogProps = {
  headline: string;
  description: string;
  submitButtonText: string;
  submitLoadingMessage: string;
  submitDisabled: boolean;
  submitting: boolean;
  onSubmit: () => void | Promise<unknown>;
  onClose: () => void;
  children: ReactNode;
};

/** The folder lives in hook state, not in the form; the form only carries submit and Enter. */
const SingleItemFolderDialog = ({
  submitting,
  onClose,
  children,
  ...props
}: SingleItemFolderDialogProps) => {
  const form = useForm({ mode: "onChange", shouldUnregister: true });

  return (
    <FormDialog
      {...props}
      open
      onClose={onClose}
      closable={!submitting}
      form={form}
      submitting={submitting}>
      {children}
    </FormDialog>
  );
};

export const useAddTouchpointToFolderModal = ({
  onSuccess,
  onRemoveSelectedItem,
}: UseAddTouchpointToFolderModalProps = {}) => {
  const [itemsToAdd, setItemsToAdd] = useState<TouchpointAttributes[]>([]);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const queryClient = useQueryClient();
  const addToFolderMutation = useAddTouchpointToFolder();

  const openAddToFolderModal = useCallback(
    (items: TouchpointAttributes | TouchpointAttributes[]) => {
      const isBulk = Array.isArray(items);
      const itemsArray = Array.isArray(items) ? items : [items];
      setItemsToAdd(itemsArray);
      setIsBulkMode(isBulk);
      setSelectedFolderId("");
    },
    [],
  );

  const closeAddToFolderModal = useCallback(() => {
    if (addToFolderMutation.isPending) return;
    setItemsToAdd([]);
    setIsBulkMode(false);
    setSelectedFolderId("");
  }, [addToFolderMutation.isPending]);

  const handleAddToFolder = useCallback(async () => {
    if (itemsToAdd.length === 0 || !selectedFolderId) return;

    if (!isBulkMode) {
      const item = itemsToAdd[0];
      addToFolderMutation.mutate(
        { folderId: selectedFolderId, data: { touchpointId: item.id } },
        {
          onSuccess: () => {
            const successTitle = item.folderId ? "Moved to folder" : "Added to folder";
            toast.success({ data: { title: successTitle } });
            onSuccess?.();
            closeAddToFolderModal();
          },
          onError: (err) => {
            const errorTitle = item.folderId
              ? "Failed to move to folder"
              : "Failed to add to folder";
            toast.error({
              data: {
                title: errorTitle,
                message: getErrorMessage(err),
              },
            });
          },
        },
      );
    } else {
      const results = await Promise.allSettled(
        itemsToAdd.map((item) =>
          addToFolderMutation.mutateAsync({
            folderId: selectedFolderId,
            data: { touchpointId: item.id },
            meta: { skipInvalidation: true },
          }),
        ),
      );

      const successIds: string[] = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          successIds.push(itemsToAdd[index].id);
        }
      });

      const successCount = successIds.length;
      const failCount = results.length - successCount;

      if (successCount) {
        queryClient.invalidateQueries({ queryKey: folderKeys.all });
        queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
        const hasSelectedWithFolder = itemsToAdd.some((tp) => !!tp.folderId);
        const successTitle = hasSelectedWithFolder
          ? "Moved creatives to folder"
          : "Added creatives to folder";
        toast.success({ data: { title: successTitle } });
        onSuccess?.();

        // Remove successful items from the list
        setItemsToAdd((prev) => prev.filter((item) => !successIds.includes(item.id)));
        successIds.forEach((id) => onRemoveSelectedItem?.(id));

        if (!failCount) {
          closeAddToFolderModal();
        }
      }

      if (failCount) {
        toast.error({
          data: {
            title: "Failed to move some creatives",
            message: getErrorMessage(results.find((r) => r.status === "rejected")?.reason),
          },
        });
      }
    }
  }, [
    itemsToAdd,
    isBulkMode,
    selectedFolderId,
    addToFolderMutation,
    queryClient,
    onSuccess,
    onRemoveSelectedItem,
    closeAddToFolderModal,
  ]);

  const handleRemoveFromList = useCallback(
    (id: string) => {
      setItemsToAdd((prev) => prev.filter((item) => item.id !== id));
      onRemoveSelectedItem?.(id);
    },
    [onRemoveSelectedItem],
  );

  const addToFolderModalMarkup = useMemo(() => {
    const isOpen = itemsToAdd.length > 0;
    if (!isOpen) return null;

    const hasSelectedWithFolder = itemsToAdd.some((tp) => !!tp.folderId);
    const itemLabel = itemsToAdd.length > 1 ? "selected items" : "selected item";
    const title = hasSelectedWithFolder ? "Move to folder" : "Add to folder";
    const description = hasSelectedWithFolder
      ? `Please choose the folder where you would like to move the ${itemLabel}.`
      : `Please choose the folder where you would like to add the ${itemLabel}.`;
    const submitButtonLabel = hasSelectedWithFolder ? "Move to folder" : "Add to folder";
    const submittingLabel = hasSelectedWithFolder ? "Moving to folder..." : "Adding to folder...";

    if (!isBulkMode) {
      const item = itemsToAdd[0];
      if (!item) return null;
      const singleTitle = item.folderId ? "Move to folder" : "Add to folder";

      return (
        <SingleItemFolderDialog
          headline={singleTitle}
          description={description}
          submitButtonText={submitButtonLabel}
          submitLoadingMessage={submittingLabel}
          submitDisabled={!selectedFolderId}
          submitting={addToFolderMutation.isPending}
          onSubmit={handleAddToFolder}
          onClose={closeAddToFolderModal}>
          <CampaignFolderSelect
            label="Folder name"
            ariaLabel="Choose folder"
            selectName="touchpoint-folder-select"
            value={selectedFolderId}
            onChange={setSelectedFolderId}
            clientId={item.clientId}
            campaignId={item.campaignId}
            enabled={isOpen}
            isRequired
          />
        </SingleItemFolderDialog>
      );
    }

    return (
      <SelectedTouchpointsFolderModal
        title={title}
        description={description}
        submitButtonLabel={submitButtonLabel}
        submittingLabel={submittingLabel}
        onCloseCallback={closeAddToFolderModal}
        onConfirmCallback={handleAddToFolder}
        selectedList={itemsToAdd}
        folderId={selectedFolderId}
        setFolderId={setSelectedFolderId}
        isSubmitDisabled={!selectedFolderId}
        isSubmitting={addToFolderMutation.isPending}
        onRemoveSelectedItem={handleRemoveFromList}
        clientId={itemsToAdd[0].clientId}
        campaignId={itemsToAdd[0].campaignId}
      />
    );
  }, [
    itemsToAdd,
    isBulkMode,
    selectedFolderId,
    addToFolderMutation.isPending,
    closeAddToFolderModal,
    handleAddToFolder,
    handleRemoveFromList,
  ]);

  return {
    openAddToFolderModal,
    addToFolderModalMarkup,
  };
};
